import type { ComponentProps } from 'react';
import React from 'react';
import styled from '@emotion/styled';
import type { TableOfContentItem, TableOfContentSlice } from 'graphql-ts-types';
import { backgroundColor, space, textAlign } from 'styled-system';

import { Link } from 'common/components/Link';
import { BodyRegularBR1, HeadlineH6 } from 'common/components/Typography';
import { textDecoration } from 'common/theme/system-utilities';
import { usePreserveQueryParams } from '../hooks/use-preserve-query-params';

export interface SeoTableProps extends Pick<
  TableOfContentSlice,
  'headline' | 'isContentCenterAligned'
> {
  headings: Array<Pick<TableOfContentItem, 'name' | 'anchor'>>;
}

const Container = styled.div<any>`
  ${textAlign};
`;

const containerStyles = {
  role: 'navigation',
};

const headlineStyles: ComponentProps<typeof HeadlineH6> = {
  as: 'h2',
  mb: 'sp_4',
  mt: 'sp_0',
  fontWeight: 'normal',
};

const UnstyledList = styled.ul<any>`
  ${space};
  ${backgroundColor};
`;

const List = (props: ComponentProps<typeof UnstyledList>) => (
  <UnstyledList
    bg="sys.neutral.background.weak"
    py={['sp_16', null, null, 'sp_20']}
    px="sp_32"
    {...props}
  />
);

const listItemStyles: ComponentProps<typeof BodyRegularBR1> = {
  as: 'li',
  color: 'sys.neutral.text.strong',
};

const UnstyledStyledLink = styled(Link)<any>`
  ${textDecoration};
`;

const StyledLink = (props: ComponentProps<typeof UnstyledStyledLink>) => (
  <UnstyledStyledLink textDecoration="underline" {...props} />
);

const SeoTable = ({ isContentCenterAligned, headline, headings }: SeoTableProps) => {
  const processedHeadings = usePreserveQueryParams(headings);

  return (
    <Container {...containerStyles} {...(isContentCenterAligned ? { textAlign: 'center' } : {})}>
      <HeadlineH6 {...headlineStyles}>{headline}</HeadlineH6>
      <List>
        {processedHeadings.map(({ name, anchor }) => (
          <BodyRegularBR1 {...listItemStyles} key={name}>
            <StyledLink href={anchor}>{name}</StyledLink>
          </BodyRegularBR1>
        ))}
      </List>
    </Container>
  );
};

export default SeoTable;
